import { StateFilter, useDocumentationContext } from "./DocumentationContext";
import { useMemo, useState, useEffect } from "react";
import { useAppState } from "../../state/State";
import { CarrierProductFilter } from "./DocumentationTypes/QuestionnaireTypes";

export const DocumentationFilters = () => {
  const {
    states,
    filterCarrierProducts,
    setFilterCarrierProducts,
    filterStates,
    setFilterStates,
    searchTerm,
    setSearchTerm,
  } = useDocumentationContext();

  const {
    state: { availableCarriersWithProducts },
  } = useAppState();

  const [isCarrierProductDropdownOpen, setIsCarrierProductDropdownOpen] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [carrierProductSearchTerm, setCarrierProductSearchTerm] = useState("");
  const [stateSearchTerm, setStateSearchTerm] = useState("");

  // Flatten available carrier-product combinations
  const allCarrierProducts = useMemo(() => {
    return availableCarriersWithProducts.flatMap(carrier =>
      carrier.products.map(product => ({
        carrier: carrier.name,
        product: product.name,
      })),
    );
  }, [availableCarriersWithProducts]);

  // Filter carrier-products based on search
  const filteredCarrierProductOptions = useMemo(
    () =>
      allCarrierProducts.filter(
        cp =>
          cp.carrier.toLowerCase().includes(carrierProductSearchTerm.toLowerCase()) ||
          cp.product.toLowerCase().includes(carrierProductSearchTerm.toLowerCase()),
      ),
    [allCarrierProducts, carrierProductSearchTerm],
  );

  // State filtering logic
  const filteredStates = useMemo(
    () =>
      states.filter(
        state =>
          state.name.toLowerCase().includes(stateSearchTerm.toLowerCase()) ||
          state.shortName.toLowerCase().includes(stateSearchTerm.toLowerCase()),
      ),
    [states, stateSearchTerm],
  );

  const toggleCarrierProduct = (cp: CarrierProductFilter) => {
    const isSelected = filterCarrierProducts.some(item => item.carrier === cp.carrier && item.product === cp.product);
    if (isSelected) {
      setFilterCarrierProducts(
        filterCarrierProducts.filter(item => !(item.carrier === cp.carrier && item.product === cp.product)),
      );
    } else {
      setFilterCarrierProducts([...filterCarrierProducts, cp]);
    }
  };

  const toggleState = (state: StateFilter) => {
    if (filterStates.some(f => f.id === state.id)) {
      setFilterStates(filterStates.filter(f => f.id !== state.id));
    } else {
      setFilterStates([...filterStates, state]);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".carrier-product-dropdown")) {
        setIsCarrierProductDropdownOpen(false);
      }
      if (!target.closest(".state-dropdown")) {
        setIsStateDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className='flex flex-col gap-4 mb-2 bg-white p-4 rounded-lg border-b sticky top-0 '>
      <div className='flex items-center gap-4'>
        <div className='flex-1'>
          <input
            type='text'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder='Search questions...'
            className='w-full px-3 py-2 border border-secondary-lighter rounded-lg focus:outline-none focus:border-primary'
          />
        </div>
        <div className='relative carrier-product-dropdown'>
          <button
            onClick={() => setIsCarrierProductDropdownOpen(!isCarrierProductDropdownOpen)}
            className='px-4 py-2 border border-secondary-lighter rounded-lg flex items-center gap-2 hover:border-primary transition-all'
          >
            <span className='text-secondary-text'>
              {filterCarrierProducts.length
                ? `${filterCarrierProducts.length} combination${filterCarrierProducts.length > 1 ? "s" : ""} selected`
                : "Select carrier-products"}
            </span>
            <span className='text-secondary-text'>▼</span>
          </button>

          {isCarrierProductDropdownOpen && (
            <div className='absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-secondary-lighter z-50'>
              <div className='p-2 border-b border-secondary-lighter'>
                <input
                  type='text'
                  value={carrierProductSearchTerm}
                  onChange={e => setCarrierProductSearchTerm(e.target.value)}
                  placeholder='Search carrier-products...'
                  className='w-full px-2 py-1 border border-secondary-lighter rounded-lg focus:outline-none focus:border-primary text-sm'
                />
              </div>
              <div className='max-h-64 overflow-y-auto'>
                {filteredCarrierProductOptions.map(cp => {
                  const key = `${cp.carrier}::${cp.product}`;
                  const isSelected = filterCarrierProducts.some(
                    item => item.carrier === cp.carrier && item.product === cp.product,
                  );
                  return (
                    <div
                      key={key}
                      className='flex items-center px-3 py-2 hover:bg-light cursor-pointer'
                      onClick={() => toggleCarrierProduct(cp)}
                    >
                      <input type='checkbox' checked={isSelected} onChange={() => {}} className='mr-2' />
                      <div className='flex-1'>
                        <div className='text-sm text-dark-blue font-medium'>{cp.product}</div>
                        <div className='text-xs text-secondary-text'>{cp.carrier}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className='relative state-dropdown'>
          <button
            onClick={() => setIsStateDropdownOpen(!isStateDropdownOpen)}
            className='px-4 py-2 border border-secondary-lighter rounded-lg flex items-center gap-2 hover:border-primary transition-all'
          >
            <span className='text-secondary-text'>
              {filterStates.length
                ? `${filterStates.length} state${filterStates.length > 1 ? "s" : ""} selected`
                : "Select states"}
            </span>
            <span className='text-secondary-text'>▼</span>
          </button>

          {isStateDropdownOpen && (
            <div className='absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-secondary-lighter z-50'>
              <div className='p-2 border-b border-secondary-lighter'>
                <input
                  type='text'
                  value={stateSearchTerm}
                  onChange={e => setStateSearchTerm(e.target.value)}
                  placeholder='Search states...'
                  className='w-full px-2 py-1 border border-secondary-lighter rounded-lg focus:outline-none focus:border-primary text-sm'
                />
              </div>
              <div className='max-h-64 overflow-y-auto'>
                {filteredStates.map(state => (
                  <div
                    key={state.id}
                    className='flex items-center px-3 py-2 hover:bg-light cursor-pointer'
                    onClick={() => toggleState(state)}
                  >
                    <input
                      type='checkbox'
                      checked={filterStates.some(f => f.id === state.id)}
                      onChange={() => {}}
                      className='mr-2'
                    />
                    <span className='text-sm text-secondary-text'>
                      {state.shortName} - {state.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {(filterCarrierProducts.length > 0 || filterStates.length > 0) && (
        <div className='flex flex-wrap gap-2'>
          {filterCarrierProducts.map(cp => {
            const key = `${cp.carrier}::${cp.product}`;
            return (
              <div key={key} className='px-3 py-1.5 bg-primary text-white text-sm rounded-full flex items-center gap-2'>
                <div
                  className='flex items-center gap-0.5
                '
                >
                  <span className='font-medium'>{cp.product}</span>
                  <span className='text-xs opacity-80'>{cp.carrier}</span>
                </div>
                <button onClick={() => toggleCarrierProduct(cp)} className='hover:text-secondary-lighter'>
                  ×
                </button>
              </div>
            );
          })}

          {filterStates.map(state => (
            <div
              key={state.id}
              className='px-2 py-1 bg-secondary text-white text-sm rounded-full flex items-center gap-1'
            >
              <span>{state.shortName}</span>
              <button onClick={() => toggleState(state)} className='hover:text-secondary-lighter ml-1'>
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
